// Quake 3 / ET multi-stage shaders on the GPU.
//
// resolveShader() applies what tr_shader.c decides per surface (implicit and
// default stages, lightmap vs vertex lighting, tcGen defaults, sort key).
// createShaderMaterials() then turns the stages into GLSL programs:
//
// - When stage 0 does not read the framebuffer, all stages are evaluated in one
//   fragment shader: every stage blends onto the running result with its own
//   blendFunc, which is exact because all stages cover the same pixel. Alpha
//   tests are done on the stage's final alpha; a failed test on stage 0
//   discards the pixel (nothing is written, so later depthFunc equal stages
//   fail too).
// - Otherwise (stage 0 blends with what is behind) every stage is its own pass:
//   separate materials on the same geometry, drawn in order.
//
// Waveforms reproduce the 4096-entry tables of tr_init.c (index truncation
// included); "noise" waves are evaluated on the CPU (R_NoiseGet4f) and passed
// as uniforms. tcGen / tcMod / rgbGen / alphaGen are computed per pixel from
// the interpolated vertex data, deformVertexes in the vertex shader.

import * as THREE from 'three';
import { blendReadsDst, SORT, type BlendFactor, type Deform, type ShaderDef, type ShaderStage, type SkyParms, type TcGen, type Wave } from './shaderScripts';
import type { TextureManager } from './textures';

export interface SharedUniforms {
  uTime: { value: number };
  uBrightness: { value: number };
}

/** Lightmap index meaning "vertex lit" (q3map2 -vertex / missing lightmap). */
export const LIGHTMAP_BY_VERTEX = -3;
export const LIGHTMAP_NONE = -1;

export interface Q3Stage extends ShaderStage {
  tcGen: TcGen;
}

export interface Q3Shader {
  name: string;
  sort: number;
  cull: 'front' | 'back' | 'none';
  polygonOffset: boolean;
  deforms: Deform[];
  stages: Q3Stage[];
  sky: SkyParms | null;
  /** Built without explicit stages (shader name / implicitMap image). */
  implicit: boolean;
  editorImage: string | null;
}

function implicitStage(map: string): ShaderStage {
  return {
    map,
    animMap: null,
    clamp: false,
    isLightmap: false,
    blend: null,
    alphaFunc: null,
    depthWrite: true,
    depthFunc: 'lequal',
    tcGen: null,
    tcMods: [],
    rgbGen: { type: 'identity' },
    alphaGen: { type: 'identity' },
    detail: false,
  };
}

/**
 * The shader as R_FindShader + FinishShader build it for one surface.
 * @param lightmapIndex surface lightmap (>= 0), LIGHTMAP_NONE or LIGHTMAP_BY_VERTEX
 */
export function resolveShader(name: string, def: ShaderDef | undefined, lightmapIndex: number): Q3Shader {
  const shader: Q3Shader = {
    name,
    sort: 0,
    cull: def?.cull ?? 'front',
    polygonOffset: def?.polygonOffset ?? false,
    deforms: def?.deforms ?? [],
    stages: [],
    sky: def?.sky ?? null,
    implicit: false,
    editorImage: def?.editorImage ?? null,
  };
  let stages: ShaderStage[];
  // A script without stages (that is not a sky) would be ET's default shader;
  // like an implicitMap we show the image named after the shader instead.
  const implicit = !def || def.implicitKind !== null || (!def.stages.length && !def.sky);
  if (implicit) {
    shader.implicit = true;
    const kind = def?.implicitKind ?? 'map';
    const image = def?.implicitMap && def.implicitMap !== '-' ? def.implicitMap : name;
    // implicitStateBits
    const state = (s: ShaderStage) => {
      if (kind === 'mask') s.alphaFunc = 'GE128';
      if (kind === 'blend') s.blend = ['GL_SRC_ALPHA', 'GL_ONE_MINUS_SRC_ALPHA'];
      return s;
    };
    if (kind !== 'map' && !def?.cullExplicit) shader.cull = 'none';
    if (lightmapIndex === LIGHTMAP_NONE) {
      stages = [state({ ...implicitStage(image), rgbGen: { type: 'lightingDiffuse' } })];
    } else if (lightmapIndex < 0) {
      stages = [state({ ...implicitStage(image), rgbGen: { type: 'exactVertex' }, alphaGen: { type: 'skip' } })];
    } else if (kind !== 'map') {
      // masked or blended implicit shaders need texture first
      stages = [state(implicitStage(image)), { ...implicitStage('$lightmap'), isLightmap: true, blend: ['GL_DST_COLOR', 'GL_ZERO'], depthFunc: 'equal' }];
    } else {
      stages = [{ ...implicitStage('$lightmap'), isLightmap: true }, { ...implicitStage(image), blend: ['GL_DST_COLOR', 'GL_ZERO'] }];
    }
  } else {
    stages = def!.stages.map((s) => ({ ...s }));
  }

  // FinishShader
  if (shader.sky) shader.sort = SORT.sky;
  else if (def?.sort != null) shader.sort = def.sort;
  if (shader.polygonOffset && !shader.sort) shader.sort = SORT.decal;
  const first = stages.find((s) => s.map !== null);
  for (const s of stages) {
    if (s.map === null) continue; // missing image: the stage is inactive
    // a lightmap stage on a vertex lit surface: white image and exact vertex colors
    if (s.isLightmap && lightmapIndex < 0 && s.map === '$lightmap') {
      s.map = '$whiteimage';
      s.rgbGen = { type: 'exactVertex' };
    }
    const tcGen: TcGen = s.tcGen ?? { type: s.isLightmap ? 'lightmap' : 'texture' };
    if (s.blend && first?.blend && !shader.sort) shader.sort = s.depthWrite ? SORT.seeThrough : SORT.blend0;
    shader.stages.push({ ...s, tcGen });
  }
  if (!shader.sort) shader.sort = SORT.opaque;
  return shader;
}

/** Can all stages run in one pass (stage 0 does not read the framebuffer)? */
export function isSinglePass(shader: Q3Shader): boolean {
  return !shader.stages.length || !blendReadsDst(shader.stages[0].blend);
}

// --- noise (tr_noise.c, MSVC rand) ---

let noiseTables: { table: Float32Array; perm: Uint8Array } | null = null;

function noise(): { table: Float32Array; perm: Uint8Array } {
  if (noiseTables) return noiseTables;
  let seed = 1001;
  const rand = () => {
    seed = (Math.imul(seed, 214013) + 2531011) >>> 0;
    return (seed >>> 16) & 0x7fff;
  };
  const table = new Float32Array(256);
  const perm = new Uint8Array(256);
  for (let i = 0; i < 256; i++) {
    table[i] = Math.fround((rand() / 32767) * 2.0 - 1.0);
    perm[i] = Math.trunc((rand() / 32767) * 255) & 255;
  }
  return (noiseTables = { table, perm });
}

/** R_NoiseGet4f */
export function noiseGet4f(x: number, y: number, z: number, t: number): number {
  const { table, perm } = noise();
  const val = (a: number) => perm[a & 255];
  const get = (ix: number, iy: number, iz: number, it: number) => table[val(ix + val(iy + val(iz + val(it))))];
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const iz = Math.floor(z);
  const it = Math.floor(t);
  const fx = x - ix;
  const fy = y - iy;
  const fz = z - iz;
  const ft = t - it;
  const lerp = (a: number, b: number, w: number) => a * (1 - w) + b * w;
  const value = [0, 0];
  for (let i = 0; i < 2; i++) {
    const front = [get(ix, iy, iz, it + i), get(ix + 1, iy, iz, it + i), get(ix, iy + 1, iz, it + i), get(ix + 1, iy + 1, iz, it + i)];
    const back = [get(ix, iy, iz + 1, it + i), get(ix + 1, iy, iz + 1, it + i), get(ix, iy + 1, iz + 1, it + i), get(ix + 1, iy + 1, iz + 1, it + i)];
    const fv = lerp(lerp(front[0], front[1], fx), lerp(front[2], front[3], fx), fy);
    const bv = lerp(lerp(back[0], back[1], fx), lerp(back[2], back[3], fx), fy);
    value[i] = lerp(fv, bv, fz);
  }
  return lerp(value[0], value[1], ft);
}

let noiseTexture: THREE.DataTexture | null = null;

/** 256x2 float texture: row 0 = noise values, row 1 = permutation (for deformVertexes normal). */
function noiseTex(): THREE.DataTexture {
  if (noiseTexture) return noiseTexture;
  const { table, perm } = noise();
  const data = new Float32Array(512);
  data.set(table, 0);
  for (let i = 0; i < 256; i++) data[256 + i] = perm[i];
  noiseTexture = new THREE.DataTexture(data, 256, 2, THREE.RedFormat, THREE.FloatType);
  noiseTexture.needsUpdate = true;
  return noiseTexture;
}

// --- waveforms ---

const FUNC_INDEX: Record<string, number> = { sin: 0, triangle: 1, square: 2, sawtooth: 3, inversesawtooth: 4, noise: 0 };
const FUNCTABLE_SIZE = 4096;

/** The ET function tables (tr_init.c), indexed like WAVEVALUE (truncation + mask). */
export function tableValue(func: Wave['func'], x: number): number {
  const i = Math.trunc(x * FUNCTABLE_SIZE) & (FUNCTABLE_SIZE - 1);
  switch (func) {
    case 'triangle':
      if (i < 1024) return i / 1024;
      if (i < 2048) return 1 - (i - 1024) / 1024;
      if (i < 3072) return -(i - 2048) / 1024;
      return -(1 - (i - 3072) / 1024);
    case 'square':
      return i < 2048 ? 1 : -1;
    case 'sawtooth':
      return i / FUNCTABLE_SIZE;
    case 'inversesawtooth':
      return 1 - i / FUNCTABLE_SIZE;
    default:
      return Math.sin((i * 2 * Math.PI) / (FUNCTABLE_SIZE - 1));
  }
}

/** EvalWaveForm (noise waves use R_NoiseGet4f like RB_CalcWaveColor). */
export function evalWave(w: Wave, time: number): number {
  if (w.func === 'noise') return w.base + noiseGet4f(0, 0, 0, (time + w.phase) * w.freq) * w.amp;
  return w.base + tableValue(w.func, w.phase + time * w.freq) * w.amp;
}

const GLSL_COMMON = /* glsl */ `
float q3table(int f, float x) {
  int i = int(x * 4096.0) & 4095;
  float u = float(i);
  if (f == 0) return sin(u * (6.283185307179586 / 4095.0));
  if (f == 1) {
    if (i < 1024) return u / 1024.0;
    if (i < 2048) return 1.0 - (u - 1024.0) / 1024.0;
    if (i < 3072) return -(u - 2048.0) / 1024.0;
    return -(1.0 - (u - 3072.0) / 1024.0);
  }
  if (f == 2) return i < 2048 ? 1.0 : -1.0;
  if (f == 3) return u / 4096.0;
  return 1.0 - u / 4096.0;
}
// base + table(phase + time * freq) * amplitude; w = (base, amp, phase, freq)
float q3wave(int f, vec4 w, float t) { return w.x + q3table(f, w.z + t * w.w) * w.y; }
`;

const GLSL_NOISE = /* glsl */ `
uniform sampler2D uNoise;
float nval(int a) { return texelFetch(uNoise, ivec2(a & 255, 1), 0).r; }
float nget(int x, int y, int z, int t) { return texelFetch(uNoise, ivec2(int(nval(x + int(nval(y + int(nval(z + int(nval(t)))))))) & 255, 0), 0).r; }
float noise4(vec4 p) {
  ivec4 i = ivec4(floor(p));
  vec4 f = p - floor(p);
  float v[2];
  for (int k = 0; k < 2; k++) {
    float f0 = mix(mix(nget(i.x, i.y, i.z, i.w + k), nget(i.x + 1, i.y, i.z, i.w + k), f.x), mix(nget(i.x, i.y + 1, i.z, i.w + k), nget(i.x + 1, i.y + 1, i.z, i.w + k), f.x), f.y);
    float b0 = mix(mix(nget(i.x, i.y, i.z + 1, i.w + k), nget(i.x + 1, i.y, i.z + 1, i.w + k), f.x), mix(nget(i.x, i.y + 1, i.z + 1, i.w + k), nget(i.x + 1, i.y + 1, i.z + 1, i.w + k), f.x), f.y);
    v[k] = mix(f0, b0, f.z);
  }
  return mix(v[0], v[1], f.w);
}
`;

const waveVec = (w: Wave) => new THREE.Vector4(w.base, w.amp, w.phase, w.freq);

interface ProgramParts {
  uniforms: Record<string, THREE.IUniform>;
  /** Per-frame CPU work (noise waves, animMap frames). */
  animate: ((time: number) => void)[];
  /** Textures to load: uniform name -> stage. */
  textures: { uniform: string; stage: Q3Stage }[];
}

// --- vertex shader ---

/**
 * GPU skinning of MDM surfaces (RB_MDM_SurfaceAnim) and entity lighting
 * (RB_CalcDiffuseColor's 16 steps) in the vertex shader: bones are three
 * texels each (matrix rows, translation in w), a vertex's weights are a run of
 * two texels per weight (bone, weight, offset) starting at its "skin" attribute.
 */
const GLSL_SKIN_DECL = /* glsl */ `uniform highp sampler2D uBones;
uniform highp sampler2D uWeights;
uniform int uWeightsWidth;
uniform vec3 uLightAmbient;
uniform vec3 uLightDirected;
uniform vec3 uLightDir;
in vec2 skin;
vec4 weightTexel(int i) { return texelFetch(uWeights, ivec2(i % uWeightsWidth, i / uWeightsWidth), 0); }
`;
const GLSL_SKIN_CODE = /* glsl */ `p = vec3(0.0);
  int first = int(skin.x);
  int count = int(skin.y);
  int firstBone = 0;
  for (int k = 0; k < 16; k++) {
    if (k >= count) break;
    vec4 a = weightTexel((first + k) * 2);
    vec4 b = weightTexel((first + k) * 2 + 1);
    int bone = int(a.x);
    if (k == 0) firstBone = bone;
    vec3 off = vec3(a.z, a.w, b.x);
    vec4 r0 = texelFetch(uBones, ivec2(0, bone), 0);
    vec4 r1 = texelFetch(uBones, ivec2(1, bone), 0);
    vec4 r2 = texelFetch(uBones, ivec2(2, bone), 0);
    p += a.y * vec3(dot(off, r0.xyz) + r0.w, dot(off, r1.xyz) + r1.w, dot(off, r2.xyz) + r2.w);
  }
  {
    vec3 m0 = texelFetch(uBones, ivec2(0, firstBone), 0).xyz;
    vec3 m1 = texelFetch(uBones, ivec2(1, firstBone), 0).xyz;
    vec3 m2 = texelFetch(uBones, ivec2(2, firstBone), 0).xyz;
    n = vec3(dot(normal, m0), dot(normal, m1), dot(normal, m2));
  }`;
const GLSL_SKIN_LIGHT = /* glsl */ `{
    float dp = clamp(floor(dot(n, uLightDir) * 16.0), 0.0, 15.0);
    vColor = vec4(min(floor(uLightAmbient + (dp / 15.0) * uLightDirected), vec3(255.0)) / 255.0, 1.0);
  }`;

function vertexShader(shader: Q3Shader, parts: ProgramParts, skinned = false): string {
  const code: string[] = [];
  let decl = '';
  if (skinned) {
    decl += GLSL_SKIN_DECL;
    // set per drawn model (entityParts.ts)
    for (const u of ['uBones', 'uWeights', 'uWeightsWidth', 'uLightAmbient', 'uLightDirected', 'uLightDir']) parts.uniforms[u] = { value: null };
    code.push(GLSL_SKIN_CODE);
  }
  let needsNoise = false;
  shader.deforms.forEach((d, k) => {
    const u = `uDeform${k}`;
    switch (d.type) {
      case 'wave': {
        decl += `uniform vec4 ${u};\nuniform float ${u}s;\n`;
        parts.uniforms[u] = { value: waveVec(d.wave) };
        parts.uniforms[`${u}s`] = { value: d.spread };
        const f = FUNC_INDEX[d.wave.func];
        if (d.wave.freq === 0) code.push(`p += n * q3wave(${f}, ${u}, uTime);`);
        else code.push(`p += n * q3wave(${f}, ${u} + vec4(0.0, 0.0, (p.x + p.y + p.z) * ${u}s, 0.0), uTime);`);
        break;
      }
      case 'move':
        decl += `uniform vec4 ${u};\nuniform vec3 ${u}v;\n`;
        parts.uniforms[u] = { value: waveVec(d.wave) };
        parts.uniforms[`${u}v`] = { value: new THREE.Vector3(...d.vector) };
        code.push(`p += ${u}v * q3wave(${FUNC_INDEX[d.wave.func]}, ${u}, uTime);`);
        break;
      case 'normal':
        needsNoise = true;
        decl += `uniform vec2 ${u};\n`;
        parts.uniforms[u] = { value: new THREE.Vector2(d.amp, d.freq) };
        code.push(
          `{ vec3 q = p * 0.98; float tt = uTime * ${u}.y;`,
          ` n.x += ${u}.x * noise4(vec4(q, tt)); n.y += ${u}.x * noise4(vec4(100.0 + q.x, q.yz, tt)); n.z += ${u}.x * noise4(vec4(200.0 + q.x, q.yz, tt)); n = normalize(n); }`,
        );
        break;
      case 'bulge':
        decl += `uniform vec3 ${u};\n`;
        parts.uniforms[u] = { value: new THREE.Vector3(d.width, d.height, d.speed) };
        code.push(`{ int off = int((4096.0 / 6.283185307179586) * (uv.x * ${u}.x + uTime * ${u}.z)); p += n * q3table(0, float(off & 4095) / 4096.0) * ${u}.y; }`);
        break;
      default:
        // autosprite, autosprite2, text, projectionShadow: not supported
        break;
    }
  });
  if (needsNoise) parts.uniforms.uNoise = { value: noiseTex() };
  return /* glsl */ `precision highp float;
precision highp int;
uniform mat4 modelMatrix;
uniform mat4 viewMatrix;
uniform mat4 projectionMatrix;
uniform float uTime;
in vec3 position;
in vec2 uv;
in vec2 lmuv;
in vec3 normal;
in vec4 color;
out vec2 vSt;
out vec2 vLm;
out vec4 vColor;
out vec3 vPos;
out vec3 vWorld;
out vec3 vNormal;
out vec2 vEnvAdjust;
${GLSL_COMMON}
${needsNoise ? GLSL_NOISE : ''}
${decl}
void main() {
  vec3 p = position;
  vec3 n = normal;
  ${code.join('\n  ')}
  vSt = uv;
  vLm = lmuv;
  ${skinned ? GLSL_SKIN_LIGHT : 'vColor = color;'}
  vPos = p;
  vec4 w = modelMatrix * vec4(p, 1.0);
  vWorld = w.xyz;
  vNormal = mat3(modelMatrix) * n;
  // RB_CalcEnvironmentTexCoords: the entity origin shifts its environment map every 256 units
  vec3 o = modelMatrix[3].xyz;
  vEnvAdjust = vec2(0.5 - fract(length(o) * 0.00390625), 0.5 - fract(o.z * 0.00390625));
  gl_Position = projectionMatrix * viewMatrix * w;
}`;
}

// --- per stage fragment code ---

const BLEND_GLSL: Record<BlendFactor, string> = {
  GL_ONE: 'vec4(1.0)',
  GL_ZERO: 'vec4(0.0)',
  GL_DST_COLOR: 'res',
  GL_ONE_MINUS_DST_COLOR: '(vec4(1.0) - res)',
  GL_SRC_ALPHA: 'vec4(c.a)',
  GL_ONE_MINUS_SRC_ALPHA: 'vec4(1.0 - c.a)',
  GL_DST_ALPHA: 'vec4(res.a)',
  GL_ONE_MINUS_DST_ALPHA: 'vec4(1.0 - res.a)',
  GL_SRC_ALPHA_SATURATE: 'vec4(vec3(min(c.a, 1.0 - res.a)), 1.0)',
  GL_SRC_COLOR: 'c',
  GL_ONE_MINUS_SRC_COLOR: '(vec4(1.0) - c)',
};

const THREE_BLEND: Record<BlendFactor, THREE.BlendingSrcFactor> = {
  GL_ONE: THREE.OneFactor,
  GL_ZERO: THREE.ZeroFactor,
  GL_DST_COLOR: THREE.DstColorFactor,
  GL_ONE_MINUS_DST_COLOR: THREE.OneMinusDstColorFactor,
  GL_SRC_ALPHA: THREE.SrcAlphaFactor,
  GL_ONE_MINUS_SRC_ALPHA: THREE.OneMinusSrcAlphaFactor,
  GL_DST_ALPHA: THREE.DstAlphaFactor,
  GL_ONE_MINUS_DST_ALPHA: THREE.OneMinusDstAlphaFactor,
  GL_SRC_ALPHA_SATURATE: THREE.SrcAlphaSaturateFactor,
  GL_SRC_COLOR: THREE.SrcColorFactor,
  GL_ONE_MINUS_SRC_COLOR: THREE.OneMinusSrcColorFactor,
};

const ALPHA_TEST: Record<string, string> = { GT0: 'c.a > 0.0', LT128: 'c.a < 0.5', GE128: 'c.a >= 0.5' };

/** GLSL computing the stage color `c` (vec4) of stage i; declares its uniforms. */
function stageColorCode(stage: Q3Stage, i: number, parts: ProgramParts, sky: boolean): { decl: string; code: string } {
  const p = `uS${i}`;
  let decl = `uniform sampler2D ${p}Tex;\n`;
  parts.uniforms[`${p}Tex`] = { value: null };
  parts.textures.push({ uniform: `${p}Tex`, stage });
  const lines: string[] = [];

  // tcGen
  const tc = stage.tcGen;
  if (sky) lines.push('vec2 st = skySt;');
  else if (tc.type === 'lightmap') lines.push('vec2 st = vLm;');
  else if (tc.type === 'environment') {
    lines.push(
      'vec3 viewer = normalize(cameraPosition - vWorld);',
      'vec3 nn = normalize(vNormal);',
      'vec3 refl = nn * (2.0 * dot(nn, viewer)) - viewer;',
      'vec2 st = vec2(vEnvAdjust.x + 0.5 * refl.y, vEnvAdjust.y - 0.5 * refl.z);',
    );
  } else if (tc.type === 'firerisenv') {
    lines.push('vec3 nn = normalize(vNormal);', 'vec3 refl = nn * (2.0 * dot(nn, vec3(0.0, 0.0, -1.0))) - vec3(0.0, 0.0, -1.0);', 'vec2 st = vec2(0.5 + refl.y * 0.5, 0.5 - refl.z * 0.5);');
  } else if (tc.type === 'vector') {
    decl += `uniform vec3 ${p}VecS;\nuniform vec3 ${p}VecT;\n`;
    parts.uniforms[`${p}VecS`] = { value: new THREE.Vector3(...tc.s) };
    parts.uniforms[`${p}VecT`] = { value: new THREE.Vector3(...tc.t) };
    lines.push(`vec2 st = vec2(dot(vPos, ${p}VecS), dot(vPos, ${p}VecT));`);
  } else lines.push('vec2 st = vSt;');

  // tcMods, in script order
  stage.tcMods.forEach((m, j) => {
    const u = `${p}M${j}`;
    switch (m.type) {
      case 'turb':
        decl += `uniform vec4 ${u};\n`;
        parts.uniforms[u] = { value: waveVec(m.wave) };
        lines.push(
          `{ float now = ${u}.z + uTime * ${u}.w;`,
          `  st += vec2(q3table(0, (vPos.x + vPos.z) * (1.0 / 128.0 * 0.125) + now), q3table(0, vPos.y * (1.0 / 128.0 * 0.125) + now)) * ${u}.y; }`,
        );
        break;
      case 'scale':
        decl += `uniform vec2 ${u};\n`;
        parts.uniforms[u] = { value: new THREE.Vector2(...m.scale) };
        lines.push(`st *= ${u};`);
        break;
      case 'scroll':
        decl += `uniform vec2 ${u};\n`;
        parts.uniforms[u] = { value: new THREE.Vector2(...m.speed) };
        lines.push(`{ vec2 a = ${u} * uTime; st += a - floor(a); }`);
        break;
      case 'stretch':
        decl += `uniform vec4 ${u};\n`;
        parts.uniforms[u] = { value: waveVec(m.wave) };
        lines.push(`{ float pp = 1.0 / q3wave(${FUNC_INDEX[m.wave.func]}, ${u}, uTime); st = st * pp + (0.5 - 0.5 * pp); }`);
        break;
      case 'transform':
        decl += `uniform vec4 ${u};\nuniform vec2 ${u}t;\n`;
        parts.uniforms[u] = { value: new THREE.Vector4(...m.matrix) };
        parts.uniforms[`${u}t`] = { value: new THREE.Vector2(...m.translate) };
        // s' = s*m00 + t*m10 + t0, t' = s*m01 + t*m11 + t1 with matrix = (m00, m01, m10, m11)
        lines.push(`st = vec2(st.x * ${u}.x + st.y * ${u}.z + ${u}t.x, st.x * ${u}.y + st.y * ${u}.w + ${u}t.y);`);
        break;
      case 'rotate':
        decl += `uniform float ${u};\n`;
        parts.uniforms[u] = { value: m.speed };
        lines.push(
          `{ int idx = int(-${u} * uTime * (4096.0 / 360.0));`,
          `  float sn = q3table(0, float(idx & 4095) / 4096.0); float cs = q3table(0, float((idx + 1024) & 4095) / 4096.0);`,
          `  st = vec2(st.x * cs - st.y * sn + 0.5 - 0.5 * cs + 0.5 * sn, st.x * sn + st.y * cs + 0.5 - 0.5 * sn - 0.5 * cs); }`,
        );
        break;
      case 'swap':
        lines.push('st = vec2(st.y, 1.0 - st.x);');
        break;
      case 'entityTranslate':
        break; // brush entities have no shaderTexCoord scroll
    }
  });
  lines.push(`vec4 c = texture(${p}Tex, st);`);

  // rgbGen
  const vertexRgb = ['vertex', 'exactVertex', 'lightingDiffuse'];
  const rgb = stage.rgbGen;
  let rgbExpr = 'vec3(1.0)';
  if (sky && vertexRgb.includes(rgb.type)) rgbExpr = 'vec3(1.0)';
  else if (vertexRgb.includes(rgb.type)) rgbExpr = 'vColor.rgb';
  else if (rgb.type === 'oneMinusVertex') rgbExpr = sky ? 'vec3(0.0)' : '(vec3(1.0) - vColor.rgb)';
  else if (rgb.type === 'oneMinusEntity') rgbExpr = 'vec3(0.0)';
  else if (rgb.type === 'const') {
    decl += `uniform vec3 ${p}Rgb;\n`;
    parts.uniforms[`${p}Rgb`] = { value: new THREE.Vector3(...rgb.color) };
    rgbExpr = `${p}Rgb`;
  } else if (rgb.type === 'wave') {
    if (rgb.wave.func === 'noise') {
      decl += `uniform float ${p}RgbN;\n`;
      const uni = (parts.uniforms[`${p}RgbN`] = { value: 0 });
      const w = rgb.wave;
      parts.animate.push((t) => (uni.value = Math.max(0, Math.min(1, evalWave(w, t)))));
      rgbExpr = `vec3(${p}RgbN)`;
    } else {
      decl += `uniform vec4 ${p}RgbW;\n`;
      parts.uniforms[`${p}RgbW`] = { value: waveVec(rgb.wave) };
      rgbExpr = `vec3(clamp(q3wave(${FUNC_INDEX[rgb.wave.func]}, ${p}RgbW, uTime), 0.0, 1.0))`;
    }
  }

  // alphaGen
  const ag = stage.alphaGen;
  const vertexAlpha = !sky && ['vertex', 'exactVertex', 'oneMinusVertex'].includes(rgb.type);
  let aExpr = '1.0';
  // ComputeColors: AGEN_IDENTITY keeps the vertex alpha only for rgbGen vertex, AGEN_SKIP keeps whatever rgbGen wrote
  if (ag.type === 'identity') aExpr = !sky && rgb.type === 'vertex' ? 'vColor.a' : '1.0';
  else if (ag.type === 'skip') aExpr = vertexAlpha ? 'vColor.a' : '1.0';
  else if (ag.type === 'vertex') aExpr = sky ? '1.0' : 'vColor.a';
  else if (ag.type === 'oneMinusVertex') aExpr = sky ? '0.0' : '(1.0 - vColor.a)';
  else if (ag.type === 'oneMinusEntity') aExpr = '0.0';
  else if (ag.type === 'const') {
    decl += `uniform float ${p}A;\n`;
    parts.uniforms[`${p}A`] = { value: ag.alpha };
    aExpr = `${p}A`;
  } else if (ag.type === 'wave') {
    if (ag.wave.func === 'noise') {
      decl += `uniform float ${p}AN;\n`;
      const uni = (parts.uniforms[`${p}AN`] = { value: 0 });
      const w = ag.wave;
      parts.animate.push((t) => (uni.value = Math.max(0, Math.min(1, evalWave(w, t)))));
      aExpr = `${p}AN`;
    } else {
      decl += `uniform vec4 ${p}AW;\n`;
      parts.uniforms[`${p}AW`] = { value: waveVec(ag.wave) };
      aExpr = `clamp(q3wave(${FUNC_INDEX[ag.wave.func]}, ${p}AW, uTime), 0.0, 1.0)`;
    }
  } else if (ag.type === 'portal') {
    decl += `uniform float ${p}Portal;\n`;
    parts.uniforms[`${p}Portal`] = { value: ag.range };
    aExpr = `clamp(length(vWorld - cameraPosition) / ${p}Portal, 0.0, 1.0)`;
  } else if (ag.type === 'lightingSpecular' && !sky) {
    // RB_CalcSpecularAlpha with its fixed light origin
    lines.push(
      '{ vec3 ld = normalize(vec3(-960.0, 1980.0, 96.0) - vPos); vec3 sn = normalize(vNormal);',
      '  vec3 rf = sn * (2.0 * dot(sn, ld)) - ld; float l = dot(rf, normalize(cameraPosition - vWorld));',
      '  c.a *= l < 0.0 ? 0.0 : min(l * l * l * l, 1.0); }',
    );
  }
  lines.push(`c *= vec4(${rgbExpr}, ${aExpr});`);
  return { decl, code: lines.join('\n    ') };
}

const FRAGMENT_HEAD = /* glsl */ `precision highp float;
precision highp int;
uniform vec3 cameraPosition;
uniform float uTime;
uniform float uBrightness;
in vec2 vSt;
in vec2 vLm;
in vec4 vColor;
in vec3 vPos;
in vec3 vWorld;
in vec3 vNormal;
in vec2 vEnvAdjust;
out vec4 fragColor;
${GLSL_COMMON}`;

/** Fragment shader evaluating `stages` in one pass (collapsed) or a single blended pass. */
function fragmentShader(stages: Q3Stage[], parts: ProgramParts, opts: { collapsed: boolean; brighten: boolean }): string {
  let decl = '';
  const body: string[] = [];
  stages.forEach((stage, i) => {
    const sc = stageColorCode(stage, i, parts, false);
    decl += sc.decl;
    const test = stage.alphaFunc ? ALPHA_TEST[stage.alphaFunc] : null;
    if (!opts.collapsed) {
      body.push(`{\n    ${sc.code}`, test ? `    if (!(${test})) discard;` : '', '    res = c;\n  }');
    } else if (i === 0) {
      // nothing behind the first stage: blend factors that only use the source
      const blend = stage.blend ? `c * ${BLEND_GLSL[stage.blend[0]].replace(/\bres\b/g, 'vec4(0.0)')}` : 'c';
      body.push(`{\n    ${sc.code}`, test ? `    if (!(${test})) discard;` : '', `    res = clamp(${blend}, 0.0, 1.0);\n  }`);
    } else {
      const [s, d] = stage.blend ?? ['GL_ONE', 'GL_ZERO'];
      body.push(`{\n    ${sc.code}`, `    if (${test ? test : 'true'}) res = clamp(c * ${BLEND_GLSL[s]} + res * ${BLEND_GLSL[d]}, 0.0, 1.0);\n  }`);
    }
  });
  return `${FRAGMENT_HEAD}
${decl}
void main() {
  vec4 res = vec4(0.0);
  ${body.filter(Boolean).join('\n  ')}
  // the canvas has an alpha channel: an opaque pass must not leave it translucent
  fragColor = vec4(${opts.brighten ? 'min(res.rgb * uBrightness, vec3(1.0))' : 'res.rgb'}, ${opts.collapsed || !stages[0].blend ? '1.0' : 'res.a'});
}`;
}

// --- materials ---

export interface MaterialContext {
  textures: TextureManager;
  shared: SharedUniforms;
  /** Lightmap of the surfaces (null when vertex lit). */
  lightmap: THREE.Texture | null;
  /** Called every frame with the shader time in seconds. */
  animators: ((time: number) => void)[];
  /** GPU skinned MDM surfaces (per model uniforms, see entityParts.ts). */
  skinned?: boolean;
  /** Low graphics: skies with one cloud layer. */
  simpleSky?: boolean;
}

/** Loads the image of a stage into a texture uniform (animMap: swaps frames over time). */
function bindStageTexture(uniform: THREE.IUniform, stage: Q3Stage, shader: Q3Shader, ctx: MaterialContext): void {
  const { textures } = ctx;
  if (stage.map === '$lightmap') {
    uniform.value = ctx.lightmap ?? textures.white;
    return;
  }
  if (!stage.map || stage.map === '$whiteimage' || stage.map.startsWith('$')) {
    uniform.value = textures.white;
    return;
  }
  uniform.value = textures.fallback;
  if (stage.animMap && stage.animMap.maps.length > 1) {
    const { freq, maps } = stage.animMap;
    const frames: (THREE.Texture | null)[] = maps.map(() => null);
    maps.forEach((m, k) => textures.load(m, stage.clamp).then((t) => (frames[k] = t)));
    ctx.animators.push((time) => {
      // R_BindAnimatedImage
      let index = Math.trunc(time * freq * FUNCTABLE_SIZE) >> 12;
      if (index < 0) index = 0;
      uniform.value = frames[index % frames.length] ?? textures.fallback;
    });
    return;
  }
  textures.load(stage.map, stage.clamp).then(async (tex) => {
    // shaders without stages fall back to their editor image when the named image is missing
    if (!tex && shader.implicit && shader.editorImage) tex = await textures.load(shader.editorImage);
    if (tex) uniform.value = tex;
  });
}

function imageExists(stage: Q3Stage, ctx: MaterialContext): boolean {
  if (!stage.map) return false;
  if (stage.animMap) return stage.animMap.maps.some((m) => ctx.textures.exists(m));
  return ctx.textures.exists(stage.map);
}

function sideOf(cull: Q3Shader['cull']): THREE.Side {
  return cull === 'none' ? THREE.DoubleSide : cull === 'back' ? THREE.BackSide : THREE.FrontSide;
}

function newParts(ctx: MaterialContext): ProgramParts {
  return { uniforms: { uTime: ctx.shared.uTime, uBrightness: ctx.shared.uBrightness }, animate: [], textures: [] };
}

function finishMaterial(mat: THREE.RawShaderMaterial, parts: ProgramParts, shader: Q3Shader, ctx: MaterialContext): THREE.RawShaderMaterial {
  for (const t of parts.textures) bindStageTexture(mat.uniforms[t.uniform], t.stage, shader, ctx);
  ctx.animators.push(...parts.animate);
  if (shader.polygonOffset) {
    mat.polygonOffset = true;
    mat.polygonOffsetFactor = -1;
    mat.polygonOffsetUnits = -2;
  }
  mat.name = shader.name;
  return mat;
}

/** Materials of a (non-sky) shader: one when the stages collapse into one pass, else one per stage. */
export function createShaderMaterials(shader: Q3Shader, ctx: MaterialContext): THREE.RawShaderMaterial[] {
  const unsupported = shader.deforms.filter((d) => d.type === 'autosprite' || d.type === 'autosprite2' || d.type === 'text' || d.type === 'projectionShadow');
  if (unsupported.length) console.info(`shader ${shader.name}: deformVertexes ${unsupported.map((d) => d.type).join(', ')} not supported`);
  // FinishShader drops stages whose image is missing. Missing base textures (and
  // multiplied ones, like a texture filtered over the lightmap) are kept and show
  // the checker fallback, so missing game data stays visible; a missing layer that
  // adds onto what is behind it must not lay a bright checker over the surface.
  const kept = shader.stages.filter((st) => !st.blend || st.blend[1] === 'GL_ZERO' || imageExists(st, ctx));
  if (shader.stages.length && !kept.length) return [];
  const stages = kept.length ? kept : [{ ...implicitStage('$whiteimage'), tcGen: { type: 'texture' } } as Q3Stage];

  if (isSinglePass({ ...shader, stages })) {
    const parts = newParts(ctx);
    const vs = vertexShader(shader, parts, ctx.skinned);
    const fs = fragmentShader(stages, parts, { collapsed: true, brighten: true });
    const mat = new THREE.RawShaderMaterial({ glslVersion: THREE.GLSL3, vertexShader: vs, fragmentShader: fs, uniforms: parts.uniforms, side: sideOf(shader.cull), depthWrite: true });
    return [finishMaterial(mat, parts, shader, ctx)];
  }

  return stages.map((stage) => {
    const parts = newParts(ctx);
    const vs = vertexShader(shader, parts, ctx.skinned);
    // brightness scales light added to the scene, not filters
    const src = stage.blend?.[0];
    const brighten = src !== 'GL_ZERO' && src !== 'GL_DST_COLOR' && src !== 'GL_ONE_MINUS_DST_COLOR';
    const fs = fragmentShader([stage], parts, { collapsed: false, brighten });
    const mat = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: vs,
      fragmentShader: fs,
      uniforms: parts.uniforms,
      side: sideOf(shader.cull),
      depthWrite: stage.depthWrite,
      depthFunc: stage.depthFunc === 'equal' ? THREE.EqualDepth : THREE.LessEqualDepth,
    });
    if (stage.blend) {
      mat.transparent = true;
      mat.blending = THREE.CustomBlending;
      mat.blendEquation = THREE.AddEquation;
      mat.blendSrc = THREE_BLEND[stage.blend[0]];
      mat.blendDst = THREE_BLEND[stage.blend[1]] as THREE.BlendingDstFactor;
      // keep the framebuffer alpha (the canvas would become see-through)
      mat.blendSrcAlpha = THREE.ZeroFactor;
      mat.blendDstAlpha = THREE.OneFactor;
      mat.blendEquationAlpha = THREE.AddEquation;
    }
    return finishMaterial(mat, parts, shader, ctx);
  });
}


// --- sky ---

const SKY_VERTEX = /* glsl */ `precision highp float;
uniform mat4 modelMatrix;
uniform mat4 viewMatrix;
uniform mat4 projectionMatrix;
in vec3 position;
out vec3 vWorld;
void main() {
  vec4 world = modelMatrix * vec4(position, 1.0);
  vWorld = world.xyz;
  gl_Position = projectionMatrix * viewMatrix * world;
}`;

/** Inverse of tr_sky.c's st_to_vec: view direction -> box side and (s, t); returns -1 side 5 (dn). */
const SKY_BOX_GLSL = /* glsl */ `
vec2 uvOf(float s, float t) { return vec2((s + 1.0) * 0.5, (1.0 - t) * 0.5); }
vec3 skyBox(vec3 d, sampler2D rt, sampler2D bk, sampler2D lf, sampler2D ft, sampler2D up, sampler2D dn) {
  vec3 a = abs(d);
  vec3 c;
  if (a.x >= a.y && a.x >= a.z) {
    if (d.x > 0.0) c = texture(rt, uvOf(-d.y / a.x, d.z / a.x)).rgb;
    else c = texture(bk, uvOf(d.y / a.x, d.z / a.x)).rgb;
  } else if (a.y >= a.z) {
    if (d.y > 0.0) c = texture(lf, uvOf(d.x / a.y, d.z / a.y)).rgb;
    else c = texture(ft, uvOf(-d.x / a.y, d.z / a.y)).rgb;
  } else {
    if (d.z > 0.0) c = texture(up, uvOf(-d.y / a.z, -d.x / a.z)).rgb;
    else c = texture(dn, uvOf(-d.y / a.z, d.x / a.z)).rgb;
  }
  return c;
}`;

const SKY_SUFFIXES = ['rt', 'bk', 'lf', 'ft', 'up', 'dn'];

/**
 * Material for the surfaces of a sky shader, evaluated per pixel from the view
 * direction: the farbox, then the cloud stages (R_InitSkyTexCoords: the ray
 * meets a sphere of radius 4096 + cloudheight around a point 4096 below the
 * eye; s/t are the acos of its direction; never on the bottom side), each
 * blended with its blendFunc, then the nearbox drawn over everything.
 */
export function createSkyMaterial(shader: Q3Shader, ctx: MaterialContext): THREE.RawShaderMaterial {
  const sky = shader.sky ?? { farbox: null, cloudHeight: 0, nearbox: null };
  const parts = newParts(ctx);
  let decl = '';
  const body: string[] = [];
  const boxUniforms = (prefix: string) => SKY_SUFFIXES.map((s) => `${prefix}_${s}`);
  const hasFar = { value: false };
  parts.uniforms.uHasFar = hasFar;
  for (const name of boxUniforms('uFar')) {
    decl += `uniform sampler2D ${name};\n`;
    parts.uniforms[name] = { value: ctx.textures.white };
  }
  const clouds = sky.cloudHeight ? shader.stages.filter((st) => imageExists(st, ctx)) : [];
  // low graphics: only the bottom cloud layer
  const stages = ctx.simpleSky ? clouds.slice(0, 1) : clouds;
  stages.forEach((stage, i) => {
    const sc = stageColorCode(stage, i, parts, true);
    decl += sc.decl;
    const [s, d] = stage.blend ?? ['GL_ONE', 'GL_ZERO'];
    const test = stage.alphaFunc ? ALPHA_TEST[stage.alphaFunc] : 'true';
    body.push(`if (clouds) {\n    ${sc.code}\n    if (${test}) res = clamp(c * ${BLEND_GLSL[s]} + res * ${BLEND_GLSL[d]}, 0.0, 1.0);\n  }`);
  });
  const hasNear = { value: false };
  if (sky.nearbox) {
    parts.uniforms.uHasNear = hasNear;
    decl += 'uniform bool uHasNear;\n';
    for (const name of boxUniforms('uNear')) {
      decl += `uniform sampler2D ${name};\n`;
      parts.uniforms[name] = { value: ctx.textures.white };
    }
    body.push('if (uHasNear) res = vec4(skyBox(d, uNear_rt, uNear_bk, uNear_lf, uNear_ft, uNear_up, uNear_dn), 1.0);');
  }
  parts.uniforms.uCloudHeight = { value: sky.cloudHeight || 512 };
  const fs = `${FRAGMENT_HEAD.replace(/in vec[234] v(St|Lm|Color|Pos|Normal|EnvAdjust);\n/g, '')}
${SKY_BOX_GLSL}
uniform bool uHasFar;
uniform float uCloudHeight;
${decl}
void main() {
  vec3 d = normalize(vWorld - cameraPosition);
  vec4 res;
  if (uHasFar) res = vec4(skyBox(d, uFar_rt, uFar_bk, uFar_lf, uFar_ft, uFar_up, uFar_dn), 1.0);
  else res = vec4(mix(vec3(0.24, 0.27, 0.32), vec3(0.45, 0.62, 0.84), clamp(d.z * 0.5 + 0.5, 0.0, 1.0)), 1.0);
  // clouds: not on the bottom side of the box
  vec3 ad = abs(d);
  bool clouds = !(d.z < 0.0 && ad.z >= ad.x && ad.z >= ad.y);
  float R = 4096.0;
  float h = uCloudHeight;
  float pp = -d.z * R + sqrt(d.z * d.z * R * R + 2.0 * R * h + h * h);
  vec3 v = normalize(d * pp + vec3(0.0, 0.0, R));
  vec2 skySt = vec2(acos(clamp(v.x, -1.0, 1.0)), acos(clamp(v.y, -1.0, 1.0)));
  // what the stage code reads from the cloud "vertices"
  vec3 vPos = cameraPosition + d * 1024.0;
  vec3 vNormal = -d;
  vec2 vEnvAdjust = vec2(0.5);
  ${body.join('\n  ')}
  fragColor = vec4(min(res.rgb * uBrightness, vec3(1.0)), 1.0);
}`;
  const mat = new THREE.RawShaderMaterial({ glslVersion: THREE.GLSL3, vertexShader: SKY_VERTEX, fragmentShader: fs, uniforms: parts.uniforms, side: THREE.DoubleSide });
  const loadBox = (name: string, prefix: string, flag: { value: boolean }) => {
    Promise.all(SKY_SUFFIXES.map((s) => ctx.textures.load(`${name}_${s}`, true))).then((images) => {
      if (!images.every(Boolean)) return;
      SKY_SUFFIXES.forEach((s, k) => {
        const img = images[k]!;
        img.generateMipmaps = false;
        img.minFilter = THREE.LinearFilter;
        img.needsUpdate = true;
        mat.uniforms[`${prefix}_${s}`].value = img;
      });
      flag.value = true;
    });
  };
  if (sky.farbox) loadBox(sky.farbox, 'uFar', hasFar);
  if (sky.nearbox) loadBox(sky.nearbox, 'uNear', hasNear);
  return finishMaterial(mat, parts, shader, ctx);
}
